<template>
  <div class="space-y-6">
    <div class="relative w-full">
      <div class="absolute top-3 left-0 right-0 h-0.5 bg-zinc-400 w-[70%] sm:w-[90%] md:w-[90%] lg:w-[95%] sm:mx-0 mx-auto pointer-events-none"></div>
      <div
          class="absolute top-3 left-16 sm:left-0 h-0.5 bg-secondary transition-all duration-500 pointer-events-none sm:mx-0 mx-auto"
          :style="{ width: progressWidth }"
      ></div>
      <div class="relative flex justify-between md:justify-between max-md:grid max-md:grid-cols-3 max-md:justify-items-center w-full">
        <div
            v-for="(step, index) in visibleSteps"
            :key="index"
            class="flex flex-col max-md:items-center max-md:text-center items-start"
        >
          <div
              class="w-6 h-6 rounded-full flex items-center justify-center transition-all duration-300"
              :class="
          index + 1 < currentStep
            ? 'bg-secondary'
          : index + 1 === currentStep
            ? 'bg-body-color border-2 border-secondary'
          : 'bg-body-color border-2 border-zinc-400'
        "
          >
            <Icon
                v-if="index + 1 < currentStep"
                name="check"
                class="w-5 mt-0.5 text-white text-lg"
            />

            <div
                v-else-if="index + 1 === currentStep"
                class="w-2.5 h-2.5 rounded-full bg-secondary"
            ></div>
          </div>

          <div class="mt-3">
            <div
                :class="[
            'text-xs font-medium uppercase tracking-wider',
            currentStep >= index + 1 ? 'text-gray-700' : 'text-gray-400'
          ]"
            >
              {{ step.label.step }}
            </div>
            <div
                :class="[
            'text-sm font-medium mt-1',
            currentStep >= index + 1 ? 'text-gray-900' : 'text-gray-500'
          ]"
            >
              {{ step.label.title }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="bg-white rounded-lg p-5 sm:p-8">
      <form @submit.prevent="handleSubmit">
        <!-- Step 1: Challenge Overview -->
        <div v-show="currentStep === 1" class="space-y-6">
          <div class="grid grid-cols-1 gap-6">
            <div class="w-full max-w-[1200px]">
              <CustomUpload
                  v-model:modelValue="form.preview_image"
                  v-model:file="files.preview_image"
                  v-model:errors="errors.preview_image"
                  :crop-width="1200"
                  :crop-height="250"
                  preview-height="h-48"
                  :disabled="!isFieldEditable('preview_image')"
                  :alt="__('Upload Preview Image')"
                  :existing-url="existingUrls.preview_image"
                  :is-random-preview="isRandomPreview"
              />
              </div>
            <div>
              <InputText
                  v-model:value="form.title"
                  :placeholder="__('Add a suggestive challenge name')"
                  id="study"
                  name="study"
                  :label="__('Title')"
                  :errors="errors.title"
                  :disabled="!isFieldEditable('title')"
                  required
              />
            </div>
            <div>
              <label class="mb-2 block text-base text-zinc-800">{{ __('Add Media') }}</label>
              <FormSingleselect
                  v-model="form.media_type"
                  :values="options.media_types"
                  :clearable="true"
                  :auto-select-first="false"
                  type="static"
                  :errors="errors.media_type"
                  :placeholder="__('Select media type')"
                  class="[&_input]:!py-2.5 [&_input]:!text-base"
                  :disabled="!isFieldEditable('media_type')"
              />
            </div>
            <div v-if="form.media_type?.id === 1" class="rounded-md border-[1px] border-gray-200 shadow sm:py-6">
              <div class="w-full max-w-[300px] mx-auto">
                <CustomUpload
                    v-model:modelValue="form.media_content"
                    v-model:file="files.media_content"
                    v-model:errors="errors.media_content"
                    :crop-width="615"
                    :crop-height="344"
                    preview-height="h-48 sm:h-40"
                    :alt="__('Upload Challenge Image')"
                    :existing-url="existingUrls.media_content"
                    :disabled="!isFieldEditable('media_content')"
                />
              </div>
            </div>

            <div v-if="form.media_type?.id === 2" class="space-y-2">
              <InputText
                  id="video_link"
                  v-model:value="form.media_content"
                  placeholder="https://"
                  class="w-full py-0"
                  :label="__('Add videos (YouTube and Cloudflare links)')"
              >
                <template #icon>
                  <Icon
                      class="text-base text-neutral-400"
                      name="link"
                      is-filled
                  />
                </template>
              </InputText>
            </div>

            <div v-if="form.media_type?.id === 3" class="space-y-2">
              <InputText
                  id="external_link"
                  v-model:value="form.media_content"
                  placeholder="https://"
                  class="w-full py-0"
                  :label="__('Add external link')"
              >
                <template #icon>
                  <Icon
                      class="text-base text-neutral-400"
                      name="link"
                      is-filled
                  />
                </template>
              </InputText>
            </div>

            <InputTextarea
                id="description"
                v-model:value="form.description"
                :errors="errors.description"
                :label="__('Description')"
                :placeholder="__('Add a challenge description')"
                class="text-base"
                :disabled="!isFieldEditable('description')"
            />

            <InputRadio
                v-if="isSingleCompany"
                id="internal"
                v-model:value="form.internal"
                :errors="errors.internal"
                :label="__('Internal Challenge')"
                :options="[
                  { id: 1, name: __('Yes') },
                  { id: 0, name: __('No') }
                ]"
                class="text-base"
                :disabled="!isFieldEditable('internal')"
                required
            />

            <div>
              <label class="mb-2 block text-base text-zinc-800">{{ __('Challenge categories') }}</label>
              <FormSingleselect
                  v-model="form.category_id"
                  :values="categories"
                  :clearable="true"
                  :auto-select-first="false"
                  type="static"
                  :errors="errors.category_id"
                  :placeholder="__('Select category')"
                  class="[&_input]:!py-2.5 [&_input]:!text-base"
                  :disabled="!isFieldEditable('category_id')"
              />
            </div>

            <div>
              <label class="mb-2 block text-base text-zinc-800">{{ __('Team') }}</label>
              <FormMultiselect
                  v-model="form.team_member_ids"
                  :values="teamMembers"
                  multiple
                  :placeholder="__('Add team members')"
                  class="[&_input]:!py-2.5 [&_input]:!text-base"
                  :disabled="!isFieldEditable('team_member_id')"
              />
            </div>

            <div class="flex items-center">
              <span class="text-base text-zinc-800 mr-4">{{ __('This challenge has a time limit') }}</span>
              <InputToggle
                  id="time_limit"
                  v-model:value="form.has_expiration"
                  @update:value="handleExpirationToggle"
                  class="inline-block"
              />
            </div>
            <InfoBox
                :show="form.has_expiration"
                :title="__('This challenge has a time limit enabled.')"
                :text="__('If you want unlimited availability, turn off the toggle.')"
            />

            <div v-if="form.has_expiration" class="grid grid-cols-2 gap-4">
              <InputDateTime
                  id="start_date"
                  v-model:value="form.start_at"
                  :errors="errors.start_at"
                  :label="__('Start date and time')"
                  :placeholder="__('Pick date and time')"
                  required
              />
              <InputDateTime
                  id="end_date"
                  v-model:value="form.end_at"
                  :errors="errors.end_at"
                  :label="__('End date and time')"
                  :placeholder="__('Pick date and time')"
                  required
              />
            </div>

            <div>
              <label class="mb-2 block text-base text-zinc-800">{{ __('Points') }}<span>*</span></label>
              <FormSingleselect
                  v-model="form.perfect_points"
                  :values="options.perfect_points_types"
                  :clearable="true"
                  :auto-select-first="false"
                  type="static"
                  :errors="errors.perfect_points"
                  :placeholder="__('Select total points gained by the participant')"
                  class="[&_input]:!py-2.5 [&_input]:!text-base"
                  :disabled="!isFieldEditable('perfect_points')"
                  required
              />
            </div>

            <div>
              <label class="mb-2 block text-base text-zinc-800">{{ __('Challenge type') }}*</label>
              <FormSingleselect
                  id="type"
                  v-model="form.type"
                  :values="options.challenge_types"
                  type="static"
                  :auto-select-first="false"
                  :errors="errors.type"
                  :placeholder="__('Select challenge type')"
                  class="[&_input]:!py-2.5 [&_input]:!text-base"
                  required
                  :disabled="!isFieldEditable('type')"
              />
            </div>

            <div class="flex items-center">
              <span class="text-base text-zinc-800 mr-4">{{ __('This challenge has a time limit for completion') }}</span>
              <InputToggle
                  id="has_time_limit"
                  v-model:value="form.has_time_limit"
                  @update:value="handleTimeLimitToggle"
                  class="inline-block"
                  :disabled="!isFieldEditable('has_time_limit')"
              />
            </div>

            <InfoBox
                :show="form.has_time_limit"
                :title="__('This challenge has a time limit enabled for completion.')"
                :text="__('If you do not wish to set a clockdown, turn off the toggle.')"
            />
            <div v-if="form.has_time_limit" class="grid grid-cols-1 gap-4">
              <InputText
                  id="time_limit_minutes"
                  v-model:value="form.time_limit_minutes"
                  :placeholder="__('Add time limit(minutes)')"
                  :label="__('Set a time limit')"
                  :errors="errors.time_limit_minutes"
                  class="w-full text-base"
                  min="1"
                  :disabled="!isFieldEditable('time_limit_minutes')"
              />
            </div>

            <div class="flex items-center">
              <span class="text-base text-zinc-800 mr-4"> {{ __('Enable Question Type Hint') }}</span>
              <InputToggle
                  id="enable_type_hint"
                  v-model:value="form.enable_type_hint"
                  class="inline-block"
                  :disabled="!isFieldEditable('enable_type_hint')"
              />
            </div>
            <InfoBox
                :show="true"
                :title='__(`Show a "Single Choice", or "Multiple Choice" label.`)'
                :text="__('This helps participants understand how to answer before they select an option.')"
            />
          </div>
        </div>

        <!-- Step 2: Questions -->
        <div v-show="currentStep === 2" class="space-y-6">
          <div :class="isSingleQuestion ? 'grid grid-cols-1' : 'grid grid-cols-1 md:grid-cols-4 gap-6'">
            <div
                v-if="!isSingleQuestion"
                class="w-full md:col-span-1 space-y-4 order-2 md:order-1"
            >
              <div
                  v-for="(q, idx) in form.questions"
                  :key="idx"
                  class="relative"
              >
                <button
                    @click="currentQuestion = idx"
                    type="button"
                    class="w-full flex flex-col justify-center pr-12 pl-4 h-12 rounded-md border transition-colors text-left overflow-hidden"
                    :class=" currentQuestion === idx
                      ? 'bg-black text-white border-black'
                      : 'bg-white text-gray-700 border-gray-200'"
                >
                  <template v-if="q.title && ((q.answers && q.answers.length > 0) || q.answer_type?.id === 3)">
                    <div class="text-sm font-semibold leading-tight">
                      {{ `Q${idx+1}_${getAnswerTypeName(q).replace(/\s+/g,'_')}: ` }}
                    </div>
                    <div class="text-xs truncate leading-tight">
                      {{ q.title }}
                    </div>
                  </template>
                  <template v-else>
                    <div class="text-sm font-semibold leading-tight">
                      {{ __('Question') }} {{ idx+1 }}
                    </div>
                  </template>
                </button>

                <button
                    v-if="form.questions.length > 1"
                    type="button"
                    @click.prevent="removeQuestion(idx)"
                    class="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center rounded-full transition-colors border"
                    :title="__('Delete question')"
                    :disabled="!isFieldEditable('questions')"
                    :class=" currentQuestion === idx
                      ? 'bg-black text-white border-black'
                      : 'bg-white text-gray-700 border-gray-200'"
                >
                  <Icon name="delete" class="w-4 h-4" />
                </button>
              </div>
              <button
                  type="button"
                  @click="addQuestion"
                  :disabled="!isFieldEditable('questions')"
                  class="w-full flex items-center justify-center mt-2 px-4 py-1 border-2 border-primary text-primary rounded-md hover:bg-primary/10 transition-colors"
              >
                <Icon name="add" class="text-primary !text-4xl" />
              </button>
            </div>

            <div
                :class="isSingleQuestion ? 'w-full' : 'w-full md:col-span-3'"
                class="space-y-6 order-1 md:order-2"
            >
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">
                  {{ __('Question Type') }}
                </label>
                <FormSingleselect
                    v-model="form.questions[currentQuestion].question_type"
                    :values="options.question_types"
                    type="static"
                    required
                    :auto-select-first="false"
                    :placeholder="__('Select type')"
                    class="[&_input]:!py-2.5 [&_input]:!text-base"
                    :disabled="!isFieldEditable('questions')"
                />
              </div>

              <div v-if="form.questions[currentQuestion].question_type?.id === 3" class="space-y-2">
                <InputText
                    :key="`q${currentQuestion}-video`"
                    :id="`question_${currentQuestion}_video`"
                    v-model:value="form.questions[currentQuestion].media_content"
                    placeholder="https://"
                    class="w-full py-0"
                    :label="__('Add videos (YouTube and Cloudflare links)')"
                >
                  <template #icon>
                    <Icon
                        class="text-base text-neutral-400"
                        name="link"
                        is-filled
                    />
                  </template>
                </InputText>
              </div>
              <div v-if="form.questions[currentQuestion].question_type?.id === 2" class="rounded-md border-[1px] border-gray-200 shadow sm:py-6">
                <div class="w-full max-w-[400px] mx-auto">
                  <CustomUpload
                      v-model="form.questions[currentQuestion].media_content"
                      v-model:file="files[`question_${currentQuestion}_media`]"
                      :errors="errors[`questions.${currentQuestion}.media_content`] || []"
                      :crop-width="400"
                      :crop-height="310"
                      preview-height="h-48"
                      :placeholder="__('Upload the main image for this question')"
                      :existing-url="existingQuestionMedia[currentQuestion]"
                  />
                </div>
              </div>

              <div>
                <InputText
                    :key="`q${currentQuestion}-title`"
                    :id="`question_${currentQuestion}_title`"
                    v-model:value="form.questions[currentQuestion].title"
                    class="w-full"
                    :errors="errors[`questions.${currentQuestion}.title`] || []"
                    :placeholder="__('Address the main question')"
                    :label="__('Question Title')"
                    required
                    :disabled="!isFieldEditable('questions')"
                />
              </div>

              <InputTextarea
                  :id="`question_${currentQuestion}_description`"
                  :key="`q${currentQuestion}-description`"
                  v-model:value="form.questions[currentQuestion].description"
                  :errors="errors[`questions.${currentQuestion}.description`] || []"
                  :label="__('Description')"
                  :placeholder="__('Add more details')"
                  class="text-base"
                  :disabled="!isFieldEditable('questions')"
              />

              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">
                  {{ __('Answer Type') }} <span class="text-red-500">*</span>
                </label>
                <FormSingleselect
                    v-model="form.questions[currentQuestion].answer_type"
                    :values="options.answers_types"
                    :errors="errors[`questions.${currentQuestion}.answer_type`] || []"
                    @update:modelValue="() => handleAnswerTypeChange(currentQuestion)"
                    type="static"
                    :auto-select-first="false"
                    required
                    :placeholder="__('Select type')"
                    class="[&_input]:!py-2.5 [&_input]:!text-base"
                    :disabled="!isFieldEditable('questions')"
                />
              </div>

              <transition
                  mode="out-in"
                  enter-active-class="transition ease-out duration-200"
                  enter-from-class="transform opacity-0 scale-95"
                  enter-to-class="transform opacity-100 scale-100"
                  leave-active-class="transition ease-in duration-150"
                  leave-from-class="transform opacity-100 scale-100"
                  leave-to-class="transform opacity-0 scale-95"
              >
                <InfoBox
                    v-if="form.questions[currentQuestion]?.answer_type?.id === 1"
                    :key="1"
                    :show="true"
                    :title="__('The users will only be able to select one option from the list provided below.')"
                    :text="__('The review is made automatically if the correct answer is marked.')"
                />

                <InfoBox
                    v-else-if="form.questions[currentQuestion]?.answer_type?.id === 2"
                    :key="2"
                    :show="true"
                    :title="__('The users have the option to select one or more answers from the list below.')"
                    :text="__('The review is made automatically if the correct answers are marked.')"
                />

                <InfoBox
                    v-else-if="form.questions[currentQuestion]?.answer_type?.id === 3"
                    :key="3"
                    :show="true"
                    :title="__('The users will be provided with a text area where they can provide their answers.')"
                    :text="__('The review should be made manually.' )"
                />
              </transition>

              <div v-if="shouldShowAnswers(form.questions[currentQuestion])" class="space-y-3">
                <div
                    v-for="(ans, aIdx) in form.questions[currentQuestion].answers"
                    :key="`${currentQuestion}-${aIdx}`"
                >
                  <Answer
                      :ans="ans"
                      :index="aIdx"
                      :answer-type="form.questions[currentQuestion].answer_type.id === 1 ? 'single' : 'multiple'"
                      :removable="form.questions[currentQuestion].answers.length > 2"
                      :errors="errors[`questions.${currentQuestion}.answers.${aIdx}`] || []"
                      @update:ans="val => form.questions[currentQuestion].answers[aIdx] = val"
                      @remove="removeAnswer(currentQuestion, aIdx)"
                      @single-selected="() => selectSingle(aIdx)"
                      :disabled="!isFieldEditable('questions')"
                  />
                </div>

                <Button
                    class="flex items-center gap-1.5"
                    variant="ghost"
                    @click="addAnswer(currentQuestion)"
                    :disabled="!isFieldEditable('questions')"
                >
                  <Icon class="text-xl" name="add_box" is-filled />
                  <span>{{ __('Add more answers') }}</span>
                </Button>

                <div v-if="errors[`questions.${currentQuestion}.correct_answer`]" class="mt-2 text-sm text-red-600">
                  {{ errors[`questions.${currentQuestion}.correct_answer`][0] }}
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Step 3: Job Assignment -->
        <div v-show="currentStep === 3" class="space-y-6">
          <div>
            <label class="block text-base font-medium text-gray-700 mb-2">
              {{ __('Link to a Job') }}
            </label>
            <FormSingleselect
                v-model="form.job_id"
                :auto-select-first="false"
                :values="jobs"
                :clearable="true"
                @clear="form.job_id = null"
                type="static"
                :errors="errors.job_id"
                class="[&_input]:!py-2.5 [&_input]:!text-base"
                :disabled="!isFieldEditable('job_id')"
            />
          </div>

          <div class="flex items-center">
            <span class="text-base text-zinc-800 mr-4"> {{ __('Challenge Instructions') }}</span>
            <InputToggle
                id="show_instructions"
                v-model:value="form.show_instructions"
                class="inline-block"
                :disabled="!isFieldEditable('instructions')"
            />
          </div>

          <InfoBox
              :show="true"
              :title="__('Give instruction about how the user should navigate the challenge.')"
              :text="__('Every instruction will be a bullet point.')"
          />

          <InputTextarea
              v-if="form.show_instructions"
              id="instructions"
              v-model:value="form.instructions"
              :errors="errors.instructions"
              :label="__('Instructions')"
              :placeholder="__('Formulate the instructions here.')"
              class="text-base"
              :disabled="!isFieldEditable('instructions')"
          />
        </div>

        <div
            class="flex flex-col sm:flex-row sm:items-center  mt-8 gap-3"
            :class="[
               currentStep > 1 ? 'sm:justify-between' : 'sm:justify-end'
            ]"
        >
          <button
              v-if="currentStep > 1"
              type="button"
              @click="previousStep"
              class="inline-flex items-center justify-center px-6 py-2 hover-lift-no-shadow text-gray-700 hover:bg-gray-50 transition-colors w-full sm:w-auto order-last sm:order-none"
              variant="ghost-no-padding"
          >
            <Icon name="arrow_back_ios" class="w-5 h-5 text-xl text-zinc-800" />
            {{ __('Go Back') }}
          </button>
          <div class="flex flex-wrap justify-center sm:justify-end gap-3 w-full sm:w-auto">
            <button
                type="button"
                @click="handleCancel"
                class="px-6 py-2 rounded-md text-primary hover:bg-primary/10 transition-colors w-full sm:w-auto order-last sm:order-none"
            >
              {{ __('Cancel') }}
            </button>
            <button
                v-if="isRestrictionMode"
                type="submit"
                :disabled="saving || !canProceed"
                :class="[
                  'px-6 py-2 rounded-md border-2 transition-colors flex items-center justify-center gap-2 w-full sm:w-auto',
                  !saving && canProceed
                    ? 'bg-primary text-white hover:bg-primary/90'
                    : 'bg-gray-300 text-gray-500 border-gray-500 cursor-not-allowed'
                ]"
            >
              <svg v-if="saving" class="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <span>{{ saving ? __('Updating...') : __('Update') }}</span>
            </button>
            <button
                v-if="mode === 'edit' && currentStep < 3 && !isRestrictionMode"
                type="button"
                @click="handleSubmit"
                :disabled="saving || !canProceed"
                :class="[
                  'px-6 py-2 rounded-md border-2 transition-colors flex items-center justify-center gap-2 w-full sm:w-auto',
                  !saving && canProceed
                    ? 'bg-none text-primary hover:bg-primary/10 border-primary'
                    : 'bg-gray-300 text-gray-500 border-gray-500 cursor-not-allowed'
                ]"
            >
              <svg v-if="saving" class="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <span>{{ saving ? __('Updating...') : __('Update Challenge') }}</span>
            </button>
            <button
                v-if="currentStep < 3"
                type="button"
                @click="nextStep"
                :disabled="stepValidating"
                :class="[
                  'px-6 py-2 rounded-md transition-colors w-full sm:w-auto',
                  canProceed
                    ? 'bg-primary text-white hover:bg-primary/90'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                ]"
            >
              <svg v-if="stepValidating" class="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <span>{{ stepValidating ? '' : __('Next') }}</span>
            </button>

            <button
                v-if="currentStep === 3 && !isRestrictionMode"
                type="submit"
                :disabled="saving"
                class="px-6 py-2 bg-primary text-white rounded-md hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed w-full sm:w-auto"
            >
              {{ saving ? (mode === 'edit' ? __('Updating...') : __('Creating...')) : (mode === 'edit' ? __('Update Challenge') : __('Publish')) }}
            </button>
          </div>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup>
import {ref, computed, onMounted, watch, onBeforeUnmount} from 'vue';
import axios from 'axios';
import CustomUpload from "@/pages/company/challenges/components/CustomUpload.vue";
import {FormMultiselect, FormSingleselect} from "@/components/global";
import { __ } from "@/helpers";
import { InputTextarea } from "@/components/form/input-textarea";
import { InputRadio } from "@/components/form/input-radio";
import InputToggle from "@/components/form/input-toggle/input-toggle.vue";
import { InputDateTime } from "@/components/form/input-date-time";
import InfoBox from "@/pages/company/challenges/components/InfoBox.vue";
import {Icon} from "@/components/block/icon";
import { InputText } from '@/components/form/input-text';
import Answer from "@/pages/company/challenges/components/Answer.vue";
import {Button} from "@/components/block/button";
import {useToast} from "vue-toastification";

const props = defineProps({
  mode: {
    type: String,
    default: 'create',
    validator: (value) => ['create', 'edit'].includes(value)
  },
  challenge: {
    type: Object,
    default: null
  },
  data: {
    type: Object,
    required: true
  },
  hasSubmissions: {
    type: Boolean,
    default: false
  },
  submissionsCount: {
    type: Number,
    default: 0
  },
  allowFullEdit: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(['success']);

const options = computed(() => props.data.options || {
  media_types: [],
  challenge_types: [],
  visibility_options: [],
  question_types: [],
  perfect_points_types: [],
  answers_types: []
});

const categories = computed(() => props.data.categories || []);
const jobs = computed(() => props.data.jobs || []);
const teamMembers = computed(() => props.data.team_members || []);
const defaultValues = computed(() => props.data.default_values || {});

const currentStep = ref(1);
const saving = ref(false);
const isLoading = ref(false);
const currentQuestion = ref(0);
const toast = useToast();

const existingUrls = ref({
  preview_image: null,
  media_content: null
});
const existingQuestionMedia = ref({});

const steps = [
  {
    label: {
      step: 'STEP 1',
      title: 'Challenge Overview'
    }
  },
  {
    label: {
      step: 'STEP 2',
      title: 'Question Editor Section'
    }
  },
  {
    label: {
      step: 'STEP 3',
      title: 'Link to a job'
    }
  }
];

const windowWidth = ref(window.innerWidth);

const isMobile = computed(() => windowWidth.value < 640)

const progressWidth = computed(() => {
  if (currentStep.value === 1) return '0%';
  if (currentStep.value === 2) {
    if (isMobile.value) {
      return '30%';
    }
    return '47%';
  }
  if (currentStep.value === 3){
    if (isMobile.value) {
      return '65%';
    }
    return '95%';
  }
  return '0%';
});

const isSingleCompany = computed(() => props.data.is_single_company || false);

const emptyQuestion = () => ({
  id: null,
  title: '',
  question_type: null,
  answer_type: null,
  answers: [],
  media_content: null,
  description: '',
});

const form = ref({
  title: '',
  description: '',
  type: defaultValues.value.type || null,
  media_type: null,
  media_content: null,
  preview_image: null,
  team_member_ids: [],
  internal: isSingleCompany.value
      ? { id: 0, name: __('No') }
      : (defaultValues.value.internal !== undefined
          ? { id: defaultValues.value.internal, name: defaultValues.value.internal === 1 ? __('Yes') : __('No') }
          : { id: 0, name: __('No') }),
  category_id: null,
  perfect_points: null,
  enable_type_hint: false,

  has_expiration: false,
  never_expires: true,
  start_at: null,
  end_at: null,

  has_time_limit: false,
  time_limit_minutes: null,

  questions: [
    emptyQuestion(),
  ],
  show_instructions: false,
  instructions: '',
  job_id: null
});

const errors = ref({
  title: [],
  description: [],
  type: [],
  media_type: [],
  media_content: [],
  preview_image: [],
  team_member_ids: [],
  internal: [],
  category_id: [],
  perfect_points: [],
  enable_type_hint: [],
  has_expiration: [],
  never_expires: [],
  start_at: [],
  end_at: [],
  has_time_limit: [],
  time_limit_minutes: [],
  questions: [],
  show_instructions: [],
  instructions: [],
  job_id: []
});

const files = ref({
  preview_image: null,
  media_content: null
});

const handleResize = () => {
  windowWidth.value = window.innerWidth;
}

onMounted(() => {
  window.addEventListener("resize", handleResize)
})

onBeforeUnmount(() => {
  window.removeEventListener("resize", handleResize)
})

onMounted(() => {
  if (props.mode === 'edit' && props.challenge) {
    initializeFormForEdit();
  }
});

const initializeFormForEdit = () => {
  const challenge = props.challenge;

  form.value = {
    title: challenge.title || '',
    description: challenge.description || '',
    type: challenge.type || defaultValues.value.type || null,
    media_type: challenge.media_type || null,
    media_content: challenge.media_content || null,
    preview_image: challenge.preview_image || null,
    is_random_preview: challenge.is_random_preview || false,
    team_member_ids: challenge.team_member_ids || challenge.teams?.map(t => t.id) || [],
    internal: challenge.internal ?? defaultValues.value.internal ?? 0,
    category_id: challenge.category_id || null,
    perfect_points: challenge.perfect_points || null,
    enable_type_hint: challenge.enable_type_hint || false,

    has_expiration: !challenge.never_expires,
    never_expires: challenge.never_expires || false,
    start_at: challenge.start_at || null,
    end_at: challenge.end_at || null,

    has_time_limit: challenge.time_limit_minutes && challenge.time_limit_minutes > 0,
    time_limit_minutes: challenge.time_limit_minutes || null,

    questions: challenge.questions?.map(q => ({
      id: q.id,
      title: q.title || '',
      question_type: q.question_type || null,
      answer_type: q.answer_type || null,
      answers: q.answers?.map(a => ({
        id: a.id,
        text: a.text || '',
        is_correct: a.is_correct || false
      })) || [],
      media_content: q.media_content || null,
      description: q.description || '',
    })) || [emptyQuestion()],

    show_instructions: !!challenge.instructions,
    instructions: challenge.instructions || '',
    job_id: challenge.job_id || null
  };

  existingUrls.value = {
    preview_image: challenge.preview_image || null,
    media_content: challenge.media_content || null
  };

  challenge.questions?.forEach((q, idx) => {
    if (q.media_content) {
      existingQuestionMedia.value[idx] = q.media_content;
    }
  });
};

const visibleSteps = computed(() => {
  return steps;
});

const canProceed = computed(() => {
  if (isRestrictionMode.value) {
    let hasValidDates = true;
    if (form.value.has_expiration) {
      hasValidDates = Boolean(form.value.start_at) && Boolean(form.value.end_at);
    }
    return hasValidDates;
  }
  if (currentStep.value === 1) {
    const hasTitle = Boolean(form.value.title?.trim());
    const typeVal = form.value.type;
    const hasType = Boolean(typeVal && (typeof typeVal === 'object' ? typeVal.id : typeVal));
    const hasInternal = form.value.internal !== null && form.value.internal !== undefined;

    let hasValidDates = true;
    if (form.value.has_expiration) {
      hasValidDates = Boolean(form.value.start_at) && Boolean(form.value.end_at);
    }

    let hasValidTimeLimit = true;
    if (form.value.has_time_limit) {
      hasValidTimeLimit = Number(form.value.time_limit_minutes) > 0;
    }

    return hasTitle && hasType && hasInternal && hasValidDates && hasValidTimeLimit;
  }

  if (currentStep.value === 2) {
    return form.value.questions.every(q => {
      const hasQTitle  = Boolean(q.title?.trim());

      const qtVal = q.question_type;
      const hasQType = Boolean(qtVal && (typeof qtVal === 'object' ? qtVal.id : qtVal));

      const atVal = q.answer_type;
      const atId = atVal
          ? (typeof atVal === 'object' ? atVal.id : atVal)
          : null;
      const hasAnsType = Boolean(atId);

      let optionsOK = true;
      if (atId === 1 || atId === 2) {
        const A = Array.isArray(q.answers) ? q.answers : [];
        optionsOK = A.length >= 2
            && A.every(a => Boolean(a.text?.trim()))
            && A.some(a => a.is_correct === true);
      }

      return hasQTitle && hasQType && hasAnsType && optionsOK;
    });
  }

  if (currentStep.value === 3) {
    if (form.value.show_instructions) {
      return Boolean(form.value.instructions?.trim());
    }
    return true;
  }

  return true;
});

const shouldShowAnswers = (question) => {
  const answerTypeId = question.answer_type?.id || question.answer_type;
  return answerTypeId === 1 || answerTypeId === 2;
};

const isFieldEditable = computed(() => (field) => {
  if (props.allowFullEdit) {
    return true;
  }

  if (props.mode === 'create' || !props.hasSubmissions) {
    return true;
  }

  const editableFields = ['start_at', 'end_at', 'never_expires', 'has_expiration'];
  return editableFields.includes(field);
});

const isRestrictionMode = computed(() => {
  if (props.allowFullEdit) {
    return false;
  }

  return props.mode === 'edit' && props.hasSubmissions;
});

function selectSingle(selectedIdx) {
  form.value.questions[currentQuestion.value].answers.forEach((a,i) => {
    a.is_correct = (i === selectedIdx);
  });
}

const handleExpirationToggle = (value) => {
  form.value.has_expiration = value;
  if (value) {
    form.value.never_expires = false;
  } else {
    form.value.never_expires = true;
    form.value.start_at = null;
    form.value.end_at = null;
  }
};

const handleTimeLimitToggle = (value) => {
  form.value.has_time_limit = value;
  if (!value) {
    form.value.time_limit_minutes = null;
  } else if (!form.value.time_limit_minutes) {
    form.value.time_limit_minutes = 60;
  }
};

const nextStep = async () => {
  if (isRestrictionMode.value) {
    currentStep.value++;
    return;
  }

  if (!canProceed.value) {
    toast.error(__('Please complete all required fields before proceeding.'))
  }

  stepValidating.value = true;

  try {
    const validationResult = await validateStep(currentStep.value);

    if (validationResult.valid) {
      currentStep.value++;
    } else {
      const errorCount = Object.keys(validationResult.errors || {}).length;
      const message = errorCount === 1
          ? __('Please fix the error before proceeding.')
          : __(`Please fix the ${errorCount} errors before proceeding.`);
      toast.error(message);
    }
  } catch (error) {
    if (error.message && error.message.includes('Extension context invalidated')) {
      console.warn('Browser extension error ignored:', error);
      currentStep.value++;
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      console.error('Validation error:', error);
      alert(__('An error occurred during validation. Please try again.'));
    }
  } finally {
    stepValidating.value = false;
  }
};

const previousStep = () => {
  if (currentStep.value > 1) {
    currentStep.value--;
  }
};

function addQuestion() {
  if (isSingleQuestion.value) {
    return;
  }
  form.value.questions.push(emptyQuestion());
  currentQuestion.value = form.value.questions.length - 1;
}

function removeQuestion(qIdx) {
  if (isSingleQuestion.value) {
    return;
  }
  if (form.value.questions.length <= 1) {
    return;
  }

  form.value.questions.splice(qIdx, 1);

  if (currentQuestion.value >= form.value.questions.length) {
    currentQuestion.value = form.value.questions.length - 1;
  }
}

function addAnswer(qIdx) {
  form.value.questions[qIdx].answers.push({ text: '', is_correct: false });
}

function removeAnswer(qIdx, aIdx) {
  if (form.value.questions[qIdx].answers.length > 2) {
    form.value.questions[qIdx].answers.splice(aIdx, 1);
  }
}

const getAnswerTypeName = (q) => {
  if (q.answer_type && typeof q.answer_type === 'object') {
    return q.answer_type.name;
  }
  const list = options.value.answers_types || [];
  const found = list.find(a => a.id === q.answer_type);
  return found ? found.name : '';
};

function handleAnswerTypeChange(qIdx) {
  const q = form.value.questions[qIdx];
  const newTypeId = q.answer_type?.id;

  if (newTypeId === 1 || newTypeId === 2) {
    if (!q.answers || q.answers.length === 0) {
      q.answers = [
        { text: '', is_correct: false },
        { text: '', is_correct: false }
      ];
    }
  } else {
    q.answers = [];
  }
}
const isRandomPreview = computed(() => {
  return form.value.is_random_preview || false;
});

const isSingleQuestion = computed(() => {
  const typeValue = form.value.type;
  if (!typeValue) return false;

  const typeId = typeof typeValue === 'object' ? typeValue.id : typeValue;

  const singleQuestionType = options.value.challenge_types?.find(
      t => t.name === 'Single Question' || t.label === 'Single Question'
  );

  return typeId === singleQuestionType?.id;
})

const uploadImage = async (image, type) => {
  const formData = new FormData();
  formData.append('image', image);
  formData.append('type', type);

  try {
    const response = await axios.post(
        route('redesign.company.challenges.upload-image'),
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          }
        }
    );
    return response.data.path;
  } catch (error) {
    toast.error(__('Failed to upload image'));
    throw error;
  }
};

watch(() => form.value.type, (newType, oldType) => {
  if (isSingleQuestion.value && form.value.questions.length > 1) {
    form.value.questions = [form.value.questions[0] || emptyQuestion()];
    currentQuestion.value = 0;
  } else if (!isSingleQuestion.value && form.value.questions.length === 0) {
    form.value.questions = [emptyQuestion()];
    currentQuestion.value = 0;
  }
});

const handleSubmit = async () => {
  isLoading.value = true;
  saving.value = true;

  try {
    let submitData;

    if (isRestrictionMode.value) {
      submitData = {
        start_at: form.value.start_at,
        end_at: form.value.has_expiration ? form.value.end_at : null,
        never_expires: !form.value.has_expiration,
      };
    } else {
      if (files.value.preview_image) {
        form.value.preview_image = await uploadImage(files.value.preview_image, 'preview_image');
      }
      if (files.value.media_content) {
        form.value.media_content = await uploadImage(files.value.media_content, 'media_content');
      }
      for (let i = 0; i < form.value.questions.length; i++) {
        const questionImage = files.value[`question_${i}_media`];
        if (questionImage && form.value.questions[i].question_type?.id === 2) {
          form.value.questions[i].media_content = await uploadImage(questionImage, 'question_media_content');
        }
      }

      submitData = {
        ...form.value,
        start_at: form.value.has_expiration ? form.value.start_at : null,
        end_at: form.value.has_expiration ? form.value.end_at : null,
        never_expires: !form.value.has_expiration,
        time_limit_minutes: form.value.has_time_limit ? form.value.time_limit_minutes : null,
        team_member_ids: form.value.team_member_ids.length > 0 ? form.value.team_member_ids : null,
      };

      delete submitData.has_expiration;
      delete submitData.has_time_limit;
    }

    let response;
    if (props.mode === 'edit') {
      response = await axios.post(
          route('redesign.company.challenges.update', { challenge: props.challenge.id }),
          submitData
      );
    } else {
      response = await axios.post(
          route('redesign.company.challenges.store'),
          submitData
      );
    }

    if (response.data.redirect) {
      window.location.href = response.data.redirect;
    } else {
      emit('success', response.data);
    }
  } catch (error) {
    if (error.response?.data?.errors) {
      errors.value = error.response.data.errors;
    }
    toast.error(__(`Error ${props.mode === 'edit' ? 'updating' : 'creating'} challenge. Please check your data.`));
  } finally {
    saving.value = false;
    isLoading.value = false;
  }
};

const handleCancel = async () => {
  window.location.href = route('company.challenges');

  if (confirm(__('Are you sure you want to cancel? Any unsaved changes will be lost.'))) {
    window.location.href = route('company.challenges');
  }
}

const stepValidating = ref(false);

const validateStep = async (step) => {
  clearStepErrors(step);

  const validationData = {
    step: step,
    ...form.value,
    start_at: form.value.has_expiration ? form.value.start_at : null,
    end_at: form.value.has_expiration ? form.value.end_at : null,
    never_expires: !form.value.has_expiration,
    time_limit_minutes: form.value.has_time_limit ? form.value.time_limit_minutes : null,
    team_member_ids: form.value.team_member_ids.length > 0 ? form.value.team_member_ids : null,
  };

  delete validationData.has_expiration;
  delete validationData.has_time_limit;

  try {
    await axios.post(route('redesign.company.challenges.validate'), validationData );
    return { valid: true };
  } catch (error) {
    if (error.response?.status === 422) {
      Object.keys(error.response.data.errors).forEach(key => {
        errors.value[key] = error.response.data.errors[key];
      });

      setTimeout(() => {
        const firstError = document.querySelector('.text-red-600:not(:empty)');
        if (firstError) {
          firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);

      return { valid: false, errors: error.response.data.errors };
    }
    throw error;
  }
};

const clearStepErrors = (step) => {
  if (step === 1) {
    const step1Fields = [
      'title',
      'description',
      'type',
      'media_type',
      'media_content',
      'preview_image',
      'internal',
      'category_id',
      'perfect_points',
      'enable_type_hint',
      'start_at',
      'end_at',
      'time_limit_minutes',
      'team_member_ids'
    ];
    step1Fields.forEach(field => {
      errors.value[field] = [];
    });
  } else if (step === 2) {
    Object.keys(errors.value).forEach(key => {
      if (key.startsWith('questions.')) {
        errors.value[key] = [];
      }
    });
  } else if (step === 3) {
    errors.value.show_instructions = [];
    errors.value.instructions = [];
    errors.value.job_id = [];
  }
};
</script>